import { useLingui } from '@lingui/react/macro';
import { getRoleLabel, ROLE_LABELS } from 'modules/teams/domain/constants/role-labels';
import { TeamParticipant } from 'modules/teams/domain/interfaces/team-participant';
import React, { useCallback } from 'react';
import { Role } from 'shared/types/role';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay, getFullName } from 'shared/utils';
import { UDSelect } from 'ud-form/components';
import { UDEntityRow } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';

/** Роли, которые можно назначить участнику. */
const ROLES: Role[] = ['STAFF', 'CAPTAIN', 'FAN'];

interface ParticipantRowProps {
  participant: TeamParticipant;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
  /** Идёт запрос по этой строке: селект и кнопка заблокированы только у неё (кнопку свой запрос запирает и сам). */
  isLoading: boolean;
  onChangeRole: (participant: TeamParticipant, role: string) => void;
  /** Возвращает промис исключения: «Исключить» заперта со спиннером до ответа. */
  onKick: (participant: TeamParticipant) => Promise<unknown>;
}

/**
 * Строка участника команды: номер, фото, ФИО, дата рождения, селект роли и
 * «Исключить».
 *
 * Формат даты рождения перенесён как есть (`getBirthDay`, возраст в скобках): у
 * состава команды он другой, сведение форматов — отдельная задача.
 */
const ParticipantRow = ({ participant, index, isLoading, onChangeRole, onKick }: ParticipantRowProps) => {
  const { t } = useLingui();
  const currentRoleLabel = getRoleLabel(participant.role);

  const handleKick = useCallback(() => onKick(participant), [onKick, participant]);

  return (
    <UDEntityRow
      index={index}
      // Фото приходит пустой строкой, а не `null`, поэтому `||`, а не `??`.
      avatar={participant.photo?.externalUrl || USER_DEFAULT_AVATAR}
      title={getFullName(participant)}
      subtitle={getBirthDay({ birthDate: new Date(participant?.birthDate ?? '') })}
      actions={
        <>
          <UDSelect
            divProps={{ style: { width: 144 } }}
            isDisabled={isLoading}
            isLoading={isLoading}
            options={ROLES.map((role) => ({
              label: t(ROLE_LABELS[role]),
              value: role,
            }))}
            value={{
              label: currentRoleLabel ? t(currentRoleLabel) : (participant.role ?? ''),
              value: participant.role,
            }}
            onChange={(value) => onChangeRole(participant, value?.value ?? '')}
          />

          <UDButton style={{ width: 144 }} variant="secondary" disabled={isLoading} onClick={handleKick}>
            {t`Исключить`}
          </UDButton>
        </>
      }
    />
  );
};

export default React.memo(ParticipantRow);
